import { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Grid, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Deal } from "src/business/deal";
import { useTranslation } from "react-i18next";
import {
  useGetRestaurantDeals,
  useUpdateRestaurantDeal,
} from "src/data/deals/queries/deals";
import { CustomButton, TipsCard } from "src/design";
import { colorTextMuted } from "src/design/tokens";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import { UserContext } from "src/Utils/Context/UserContext";
import { isFeatureSectionEnabled } from "src/helpers/FeatureCapabilityConfig";
import { StatsRow } from "./components/StatsRow";
import { DealsList } from "./components/DealsList";
import { CreateDealModal } from "./components/CreateDealModal";

interface Props {
  id?: string;
  compact?: boolean;
}

export const Deals = ({ id, compact = false }: Props) => {
  const { restaurantId: restaurantIdFromParams } = useParams<{ restaurantId: string }>();
  const resolvedId = id || restaurantIdFromParams || "";
  const { t } = useTranslation();

  const { data: deals = [], refetch } = useGetRestaurantDeals({ restaurantId: resolvedId });
  const { mutate: updateDeal } = useUpdateRestaurantDeal();

  const { user } = useContext(UserContext);
  const { restaurants } = useContext(RestaurantContext);
  const restaurantCapabilities = restaurants
    .find((r) => r.id === resolvedId)
    ?.capabilities.map((c) => c.name);

  const isDealCreateEnabled = isFeatureSectionEnabled(
    ["deals_create"],
    "deals_management",
    user,
    restaurantCapabilities,
  );
  const isDealUpdateEnabled = isFeatureSectionEnabled(
    ["deals_update"],
    "deals_management",
    user,
    restaurantCapabilities,
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | undefined>();

  const handleToggleStatus = (deal: Deal) => {
    updateDeal(
      {
        restaurantId: resolvedId,
        dealId: deal.id,
        payload: {
          content: deal.content,
          active: deal.active,
          type: deal.type,
          days: deal.days,
          timeRanges: deal.timeRanges
        },
      },
      { onSuccess: () => refetch() }
    );
  };

  const handleEdit = (deal: Deal) => {
    setSelectedDeal(deal);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedDeal(undefined);
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3, width: "100%" }}>
      <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
        <Grid item xs={12} sm>
          <Typography sx={{ fontWeight: 700, fontSize: "24px", lineHeight: 1.3 }}>
            {t("deals.pageTitle")}
          </Typography>
          <Typography sx={{ fontSize: "14px", color: colorTextMuted, mt: 0.5 }}>
            {t("deals.pageSubtitle")}
          </Typography>
        </Grid>
        {!compact && isDealCreateEnabled && (
          <Grid item xs={12} sm="auto">
            <CustomButton
              color="black"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setModalOpen(true)}
              sx={{ whiteSpace: "nowrap", width: { xs: "100%", sm: "auto" } }}
            >
              {t("deals.createButton")}
            </CustomButton>
          </Grid>
        )}
      </Grid>

      <StatsRow deals={deals} />

      <DealsList
        deals={deals}
        canUpdate={isDealUpdateEnabled}
        onToggle={handleToggleStatus}
        onEdit={handleEdit}
      />

      <Box sx={{ mt: 3 }}>
        <TipsCard
          title={t("deals.tips.title")}
          tips={[
            t("deals.tips.urgency"),
            t("deals.tips.multiSlot"),
            t("deals.tips.expiry"),
            t("deals.tips.slowDays"),
          ]}
        />
      </Box>

      <CreateDealModal
        key={`${modalOpen}-${selectedDeal?.id ?? "new"}`}
        open={modalOpen}
        onClose={handleModalClose}
        restaurantId={resolvedId}
        deal={selectedDeal}
        onSuccess={refetch}
      />
    </Box>
  );
};
